

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createRole } from "../redux/actions/roleActions";
import { 
  FiPlus, 
  FiCheck, 
  FiArrowLeft, 
  FiUser, 
  FiFileText, 
  FiAlertCircle,
  FiShield
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const CreateRole = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, success } = useSelector((state) => state.role);

  const [formData, setFormData] = useState({
    role_name: "",
    description: ""
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.role_name.trim()) return;

    const payload = {
      role_name: formData.role_name.trim(),
      description: formData.description.trim()
    };

    dispatch(createRole(payload));
  };

  // Reset form when creation is successful
  React.useEffect(() => {
    if (success && !loading) {
      setFormData({
        role_name: "",
        description: ""
      });
    }
  }, [success, loading]);

  const isFormValid = formData.role_name.trim() !== "";

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Creating Role...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        {/* <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard/roles")}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <FiArrowLeft className="text-lg" />
            Back to Roles
          </button>
        </div> */}

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <FiShield className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  Create Role
                </h1>
                <p className="text-blue-100">
                  Add a new role with specific permissions and access levels
                </p>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6">
            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <FiCheck className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-green-800 dark:text-green-300 font-medium">
                      Role created successfully!
                    </p>
                    <p className="text-green-600 dark:text-green-400 text-sm">
                      The new role has been added to the system.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <FiAlertCircle className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-red-800 dark:text-red-300 font-medium">
                      {typeof error === "string" ? error : error?.message || "Something went wrong"}
                    </p>
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                      Please check your input and try again.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                {/* Role Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiUser className="text-sm" />
                      Role Name <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    value={formData.role_name}
                    onChange={(e) => handleChange("role_name", e.target.value)}
                    placeholder="Enter role name (e.g., Admin, Manager, Employee, Supervisor, etc.)"
                    className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition placeholder-gray-500 dark:placeholder-gray-400"
                    required
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    This will be the primary identifier for the role in the system
                  </p>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiFileText className="text-sm" />
                      Description
                    </div>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    placeholder="Enter role description (optional) - you can include responsibilities, permissions, and access levels..."
                    rows={4}
                    className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Optional: Provide details about responsibilities and permissions for this role
                  </p>
                </div>
              </div>

              {/* Common Roles Preview */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                  <FiShield className="text-sm" />
                  Common Role Types
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {['Admin', 'Manager', 'Employee', 'Supervisor', 'Team Lead', 'Viewer', 'Editor', 'Guest'].map((role) => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => handleChange("role_name", role)}
                      className="text-xs bg-white dark:bg-gray-700 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 py-2 px-3 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors text-center"
                    >
                      {role}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  Click on any role above to quickly fill the role name field
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      role_name: "",
                      description: ""
                    });
                  }}
                  disabled={loading}
                  className="flex items-center justify-center gap-2 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear Form
                </button>
                
                <button
                  type="submit"
                  disabled={loading || !isFormValid}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white py-3 px-6 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating Role...
                    </>
                  ) : (
                    <>
                      <FiCheck className="text-lg" />
                      Create Role
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Form Tips */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
            💡 Role Creation Guidelines
          </h3>
          <ul className="text-blue-800 dark:text-blue-200 text-sm space-y-2">
            <li>• Use clear and descriptive role names that reflect the position's responsibilities</li>
            <li>• Common roles include: Admin, Manager, Employee, Supervisor, Team Lead</li>
            <li>• Consider your organization's hierarchy when creating new roles</li>
            <li>• Role names should be unique and easily identifiable</li>
            <li>• Descriptions help clarify the scope and permissions of each role</li>
            <li>• You can assign permissions to roles after creation</li>
            <li>• Fields marked with * are required</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CreateRole;